import React, { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { Header } from "@/components/Header";
import { StickyCartBar } from "@/components/StickyCartBar";
import { PincodeGate } from "@/components/PincodeGate";
import { FloatingActions } from "@/components/FloatingActions";
import { ProtectedRoute } from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
import CombosPage from "@/pages/CombosPage";
import QuickOrderPage from "@/pages/QuickOrderPage";
import CategoryPage from "@/pages/CategoryPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import ComboDetailPage from "@/pages/ComboDetailPage";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import OrdersPage from "@/pages/OrdersPage";
import OrderDetailPage from "@/pages/OrderDetailPage";
import LoginPage from "@/pages/LoginPage";
import WishlistPage from "@/pages/WishlistPage";
import AddressesPage from "@/pages/AddressesPage";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminProductForm from "@/pages/admin/AdminProductForm";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminCombos from "@/pages/admin/AdminCombos";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminRestricted from "@/pages/admin/AdminRestricted";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminBulkUpload from "@/pages/admin/AdminBulkUpload";
import AdminCarts from "@/pages/admin/AdminCarts";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminUserDetail from "@/pages/admin/AdminUserDetail";
import AdminSettings from "@/pages/admin/AdminSettings";

function Shell({ children }) {
  const [pinOpen, setPinOpen] = useState(false);
  return (
    <>
      <Header onOpenPincode={() => setPinOpen(true)} />
      <main className="pb-28">{children}</main>
      <StickyCartBar />
      <FloatingActions />
      <PincodeGate open={pinOpen} onOpenChange={setPinOpen} />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
      <AuthProvider>
        <CartProvider>
          <WishlistProvider>
          <BrowserRouter>
            <Routes>
              {/* Public site with shell */}
              <Route path="/" element={<Shell><Home /></Shell>} />
              <Route path="/combos" element={<Shell><CombosPage /></Shell>} />
              <Route path="/standard_crackers_pricelist" element={<Shell><QuickOrderPage /></Shell>} />
              <Route path="/quick-order" element={<Navigate to="/standard_crackers_pricelist" replace />} />
              <Route path="/combos/:slug" element={<Shell><ComboDetailPage /></Shell>} />
              <Route path="/category/:slug" element={<Shell><CategoryPage /></Shell>} />
              <Route path="/products/:slug" element={<Shell><ProductDetailPage /></Shell>} />
              <Route path="/cart" element={<Shell><CartPage /></Shell>} />
              <Route path="/checkout" element={
                <ProtectedRoute><Shell><CheckoutPage /></Shell></ProtectedRoute>
              } />
              <Route path="/orders" element={
                <ProtectedRoute><Shell><OrdersPage /></Shell></ProtectedRoute>
              } />
              <Route path="/orders/:orderId" element={
                <ProtectedRoute><Shell><OrderDetailPage /></Shell></ProtectedRoute>
              } />
              <Route path="/wishlist" element={
                <ProtectedRoute><Shell><WishlistPage /></Shell></ProtectedRoute>
              } />
              <Route path="/account/addresses" element={
                <ProtectedRoute><Shell><AddressesPage /></Shell></ProtectedRoute>
              } />
              <Route path="/login" element={<LoginPage />} />

              {/* Admin */}
              <Route path="/admin" element={
                <ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>
              }>
                <Route index element={<AdminDashboard />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="products/new" element={<AdminProductForm />} />
                <Route path="products/:productId" element={<AdminProductForm />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="combos" element={<AdminCombos />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="carts" element={<AdminCarts />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="restricted" element={<AdminRestricted />} />
                <Route path="coupons" element={<AdminCoupons />} />
                <Route path="bulk-upload" element={<AdminBulkUpload />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="users/:userId" element={<AdminUserDetail />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-center" richColors />
          </WishlistProvider>
        </CartProvider>
      </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
